import { Link, useLocation, useNavigate } from "react-router-dom";
import { adminLogout } from "../lib/api";

// Single source of truth for the admin nav. Rendered on every /admin/* page
// via <AdminShell>. Adding or reordering menu items happens here only.
const NAV = [
    { to: "/admin/dashboard", label: "Dashboard" },
    { to: "/admin/recognition", label: "Recognition" },
    { to: "/admin/projects", label: "Projects" },
    { to: "/admin/services-page", label: "Services" },
    { to: "/admin/studio", label: "Studio" },
    { to: "/admin/articles", label: "Studio Life" },
    { to: "/admin/clients", label: "Clients" },
    { to: "/admin/media", label: "Media" },
    { to: "/admin/inquiries", label: "Inquiries" },
    { to: "/admin/settings/email", label: "Email" },
    { to: "/admin/settings", label: "Settings" },
];

export function AdminNav() {
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const logout = async () => {
        try {
            await adminLogout();
        } finally {
            navigate("/admin/login", { replace: true });
        }
    };

    const isActive = (to) => {
        // Longest-prefix match, but /admin/settings must not match /admin/settings/email.
        if (to === "/admin/settings") return pathname === "/admin/settings";
        return pathname === to || pathname.startsWith(`${to}/`);
    };

    return (
        <nav
            data-testid="admin-nav"
            className="flex items-center gap-x-6 gap-y-2 flex-wrap"
        >
            {NAV.map((item) => {
                const active = isActive(item.to);
                return (
                    <Link
                        key={item.to}
                        to={item.to}
                        data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        aria-current={active ? "page" : undefined}
                        className={`text-[11px] uppercase tracking-[0.22em] transition-colors ${
                            active
                                ? "text-[#1A1A1A] border-b border-[#1A1A1A] pb-1"
                                : "text-[#5C5855] hover:text-[#1A1A1A]"
                        }`}
                    >
                        {item.label}
                    </Link>
                );
            })}
            <button
                onClick={logout}
                data-testid="admin-nav-sign-out"
                className="text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]"
            >
                Sign out
            </button>
        </nav>
    );
}

// Small header wrapper so pages can share the same top-band layout with one line.
export function AdminHeader({ eyebrow = "Studio Administration", title }) {
    return (
        <header className="flex items-end justify-between border-b border-[#E5E1DB] pb-8 flex-wrap gap-y-6">
            <div>
                <p className="asc-overline">{eyebrow}</p>
                <h1 className="serif text-3xl lg:text-4xl mt-2 tracking-tight">{title}</h1>
            </div>
            <AdminNav />
        </header>
    );
}

export default AdminNav;
